/**
 * Critical-path analysis — SCM5.
 *
 * Pure function. Given a BoM and a target in-service date (COD), compute
 * for each item:
 *
 *   latestOrderDate = cod - installDuration - shipDuration - leadTimeHigh
 *   slackMonths     = (latestOrderDate - today) / month
 *   isOnCriticalPath = slackMonths < 3        // tunable
 *
 * Items with `slackMonths < 0` are already late vs the COD target — they
 * surface as red banners. Items with `0 ≤ slackMonths < 3` are amber.
 *
 * MVP simplifications (deliberate, called out so they're easy to upgrade):
 *   - Install + ship duration default to 0 months; the spec says these
 *     should come off the BoM item or a per-category default table —
 *     until SCM-Data lands those defaults, callers pass an override.
 *   - The "today" reference time is injectable so tests can pin it
 *     deterministically.
 *   - We use `leadTimeMonthsHigh` (worst-case) as the planning horizon.
 *     Awarded quotes can override on the BoM item; this function reads
 *     whatever lead time the item currently carries (no quote-merging
 *     here — that's a higher-level concern).
 *
 * Units: all months are integer-ish (we use floating-point months and
 * round only at display time). Dates are ISO strings.
 */
import type { BomItem, SupplyChainPlan } from '../types/supplyChain';

export interface CriticalPathItem {
  bomItemId: string;
  name: string;
  category: BomItem['category'];
  leadTimeMonthsHigh: number;
  /** ISO date — the latest the item can be ordered without slipping COD. */
  latestOrderDate: string;
  /** Months of slack remaining vs `today`. Negative = already late. */
  slackMonths: number;
  /** User designated `criticality === 'critical-path'` on the BoM item. */
  isUserFlagged: boolean;
  /** Schedule slack below threshold (requires valid COD). */
  isScheduleAtRisk: boolean;
  /** User-flagged OR schedule-at-risk. */
  isOnCriticalPath: boolean;
  /** Worst case (`slackMonths < 0`) — must order today or earlier. */
  isOverdue: boolean;
}

export interface CriticalPathResult {
  /** All items sorted ascending by slack (most-late first). */
  items: CriticalPathItem[];
  /** Just the subset with `isOnCriticalPath`. */
  criticalPathItems: CriticalPathItem[];
  /** Subset user-flagged via BoM `criticality === 'critical-path'`. */
  userFlaggedItems: CriticalPathItem[];
  /** Subset schedule-at-risk only (slack threshold, excludes user-only flags). */
  scheduleAtRiskItems: CriticalPathItem[];
  /** Sum of -slack across overdue items (≥ 0). Higher = more scheduling risk. */
  totalNegativeSlackMonths: number;
  /** Sentinel — true when at least one item is overdue. */
  hasOverdueItems: boolean;
  /** ISO timestamp this analysis was run at (echoed `now`). */
  computedAt: string;
  /** ISO `inServiceDate` echoed back. `null` when the caller had no COD. */
  inServiceDate: string | null;
}

/** Items with less than this much slack are flagged as critical-path. */
export const CRITICAL_PATH_SLACK_THRESHOLD_MONTHS = 3;

/** Conventional month length in milliseconds — good to ~0.4% accuracy. */
const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

export interface ComputeCriticalPathOptions {
  /** Override the "current time" — required for deterministic tests. */
  now?: Date | string;
  /**
   * Per-item or default install duration in months. Reserved for future
   * use when BoM items carry an installation estimate; today we treat
   * any unmapped item as 0.
   */
  installDurationMonths?: (item: BomItem) => number;
  /**
   * Per-item ship duration in months. Reserved likewise — defaults to 0.
   */
  shipDurationMonths?: (item: BomItem) => number;
}

function isCriticalPathDesignated(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase().replace(/[_\s]+/g, '-');
  return normalized === 'critical-path';
}

/**
 * Compute the critical-path snapshot for a BoM against a COD target.
 *
 * When `inServiceDate` is null, schedule slack is unavailable but
 * user-flagged items (`criticality === 'critical-path'`) still surface.
 */
export function computeCriticalPath(
  bom: BomItem[],
  inServiceDate: string | null,
  options: ComputeCriticalPathOptions = {},
): CriticalPathResult {
  const nowRef = options.now ? new Date(options.now) : new Date();
  const nowIso = nowRef.toISOString();
  const cod = inServiceDate ? new Date(inServiceDate) : null;
  const codValid = cod !== null && !Number.isNaN(cod.getTime());
  const installFn = options.installDurationMonths ?? (() => 0);
  const shipFn = options.shipDurationMonths ?? (() => 0);

  const items: CriticalPathItem[] = bom.map((item) => {
    const installMo = installFn(item);
    const shipMo = shipFn(item);
    const leadHigh = item.leadTimeMonthsHigh;
    const isUserFlagged = isCriticalPathDesignated(item.criticality);
    if (!codValid) {
      return {
        bomItemId: item.id,
        name: item.name,
        category: item.category,
        leadTimeMonthsHigh: leadHigh,
        latestOrderDate: '',
        slackMonths: Number.POSITIVE_INFINITY,
        isUserFlagged,
        isScheduleAtRisk: false,
        isOnCriticalPath: isUserFlagged,
        isOverdue: false,
      };
    }
    const totalLeadMs = (installMo + shipMo + leadHigh) * MS_PER_MONTH;
    const latestOrder = new Date((cod as Date).getTime() - totalLeadMs);
    const slackMs = latestOrder.getTime() - nowRef.getTime();
    const slackMonths = slackMs / MS_PER_MONTH;
    const isScheduleAtRisk = slackMonths < CRITICAL_PATH_SLACK_THRESHOLD_MONTHS;
    return {
      bomItemId: item.id,
      name: item.name,
      category: item.category,
      leadTimeMonthsHigh: leadHigh,
      latestOrderDate: latestOrder.toISOString(),
      slackMonths,
      isUserFlagged,
      isScheduleAtRisk,
      isOnCriticalPath: isUserFlagged || isScheduleAtRisk,
      isOverdue: slackMonths < 0,
    };
  });

  // Ascending by slack — most-late first so consumers can `.slice(0, N)`
  // for "top N risks". Items with infinite slack sort last naturally.
  items.sort((a, b) => a.slackMonths - b.slackMonths);

  const criticalPathItems = items.filter((i) => i.isOnCriticalPath);
  const userFlaggedItems = items.filter((i) => i.isUserFlagged);
  const scheduleAtRiskItems = items.filter((i) => i.isScheduleAtRisk && !i.isUserFlagged);
  const totalNegativeSlackMonths = items
    .filter((i) => i.isOverdue)
    .reduce((sum, i) => sum + Math.abs(i.slackMonths), 0);

  return {
    items,
    criticalPathItems,
    userFlaggedItems,
    scheduleAtRiskItems,
    totalNegativeSlackMonths,
    hasOverdueItems: items.some((i) => i.isOverdue),
    computedAt: nowIso,
    inServiceDate: inServiceDate ?? null,
  };
}

/** Convenience overload — accept a SupplyChainPlan instead of a raw BoM. */
export function computeCriticalPathForPlan(
  plan: SupplyChainPlan | undefined,
  inServiceDate: string | null,
  options: ComputeCriticalPathOptions = {},
): CriticalPathResult {
  return computeCriticalPath(plan?.bom ?? [], inServiceDate, options);
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-phase critical path (PP pack — PP7).
//
// When a BoM is generated per phase (via `generateBomFromPhases`), each
// item carries a `phaseId` tag. This helper splits the BoM by phaseId and
// computes a per-phase CriticalPathResult against each phase's own COD.
//
// Items without a `phaseId` (e.g. universal lines that span all phases —
// not common, but legal) are aggregated into a synthetic `__all__` slot
// computed against the project's ultimate COD.
// ─────────────────────────────────────────────────────────────────────────────

export interface ProjectPhaseLike {
  id: string;
  sequence: number;
  targetInServiceDate: string | null;
}

export interface CriticalPathPerPhaseResult {
  /** Map of phaseId → critical-path result computed against the phase COD. */
  byPhase: Record<string, CriticalPathResult>;
  /** Phase ids in `sequence` order — handy for renderers. */
  phaseOrder: string[];
  /** Aggregate union: every item from every phase, sorted by slack. */
  aggregate: CriticalPathResult;
  /** Phase ids that are overdue (any item with slack < 0). */
  overduePhaseIds: string[];
}

/**
 * Compute critical-path per phase. Each phase's items are evaluated
 * against THAT phase's `targetInServiceDate` (not the project ultimate).
 *
 * Items lacking `phaseId` get computed against the project ultimate COD
 * in the synthetic `__all__` slot. The aggregate is the union of all
 * per-phase items (still sorted by slack).
 */
export function computeCriticalPathPerPhase(
  bom: BomItem[],
  phases: ProjectPhaseLike[],
  ultimateInServiceDate: string | null,
  options: ComputeCriticalPathOptions = {},
): CriticalPathPerPhaseResult {
  const phasesSorted = phases.slice().sort((a, b) => a.sequence - b.sequence);
  const phaseById = new Map<string, ProjectPhaseLike>();
  for (const p of phasesSorted) phaseById.set(p.id, p);

  // Bucket items by phaseId.
  const buckets = new Map<string, BomItem[]>();
  const unphased: BomItem[] = [];
  for (const item of bom) {
    if (item.phaseId && phaseById.has(item.phaseId)) {
      const arr = buckets.get(item.phaseId) ?? [];
      arr.push(item);
      buckets.set(item.phaseId, arr);
    } else {
      unphased.push(item);
    }
  }

  const byPhase: Record<string, CriticalPathResult> = {};
  for (const phase of phasesSorted) {
    const items = buckets.get(phase.id) ?? [];
    byPhase[phase.id] = computeCriticalPath(items, phase.targetInServiceDate, options);
  }
  if (unphased.length > 0) {
    byPhase['__all__'] = computeCriticalPath(unphased, ultimateInServiceDate, options);
  }

  const aggregateItems: CriticalPathResult['items'] = [];
  let totalNeg = 0;
  let anyOverdue = false;
  for (const key of Object.keys(byPhase)) {
    const r = byPhase[key];
    aggregateItems.push(...r.items);
    totalNeg += r.totalNegativeSlackMonths;
    anyOverdue = anyOverdue || r.hasOverdueItems;
  }
  aggregateItems.sort((a, b) => a.slackMonths - b.slackMonths);

  const overduePhaseIds = phasesSorted
    .filter(p => byPhase[p.id]?.hasOverdueItems)
    .map(p => p.id);

  return {
    byPhase,
    phaseOrder: phasesSorted.map(p => p.id),
    aggregate: {
      items: aggregateItems,
      criticalPathItems: aggregateItems.filter(i => i.isOnCriticalPath),
      userFlaggedItems: aggregateItems.filter(i => i.isUserFlagged),
      scheduleAtRiskItems: aggregateItems.filter(i => i.isScheduleAtRisk && !i.isUserFlagged),
      totalNegativeSlackMonths: totalNeg,
      hasOverdueItems: anyOverdue,
      computedAt: options.now ? new Date(options.now).toISOString() : new Date().toISOString(),
      inServiceDate: ultimateInServiceDate ?? null,
    },
    overduePhaseIds,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// D1 (Wave D activation) — phase-aware resolution facade.
//
// WHY: every report/UI surface previously called `computeCriticalPathForPlan`,
// which scores EVERY BoM line against ONE project COD (the project's ultimate
// in-service date, in practice phase-1's date when passed `inServiceDate`).
// For a phased rollout, a Phase-4 SMR line whose own COD is years later than
// Phase-1 was being measured against Phase-1's COD — fabricating overdue/at-risk
// flags. `computeCriticalPathPerPhase` already existed (PP7) but had ZERO
// production callers (verified by grep) so every surface silently collapsed to
// one COD.
//
// This facade lets a surface pass the project's phases (from
// `effectivePhases(intent).phases`) WITHOUT changing what it consumes: it
// returns the same flat `CriticalPathResult`. When there are genuinely ≥2
// phases AND at least one BoM line carries a `phaseId`, it routes through the
// per-phase engine and returns the AGGREGATE (every line scored against ITS OWN
// phase COD, unioned and re-sorted by slack). Otherwise — single-phase, no
// phases supplied, or a BoM with no phase tags — it is byte-identical to the
// legacy `computeCriticalPath` against `ultimateInServiceDate`.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Phase-aware critical-path resolution. Returns a flat `CriticalPathResult`
 * (drop-in for `computeCriticalPathForPlan`) but scores each line against its
 * own phase COD when the project is genuinely multi-phase and the BoM is
 * phase-tagged.
 *
 * `phases` should come from `effectivePhases(intent).phases` mapped to
 * `ProjectPhaseLike`. `ultimateInServiceDate` is the project COD used both as
 * the single-phase anchor (fallback) and for any unphased `__all__` lines.
 */
export function computeCriticalPathResolved(
  plan: SupplyChainPlan | undefined,
  ultimateInServiceDate: string | null,
  phases: ProjectPhaseLike[] | undefined,
  options: ComputeCriticalPathOptions = {},
): CriticalPathResult {
  const bom = plan?.bom ?? [];
  const phaseList = phases ?? [];
  // Only diverge from the legacy single-COD path when the project is genuinely
  // multi-phase AND the BoM is phase-tagged — otherwise the per-phase engine
  // would bucket everything into `__all__` and produce an identical result with
  // extra work. This keeps single-phase / untagged plans byte-identical.
  const isMultiPhase = phaseList.length >= 2;
  const hasPhasedBom = isMultiPhase && bom.some(item => {
    if (!item.phaseId) return false;
    return phaseList.some(p => p.id === item.phaseId);
  });
  if (!hasPhasedBom) {
    return computeCriticalPath(bom, ultimateInServiceDate, options);
  }
  return computeCriticalPathPerPhase(bom, phaseList, ultimateInServiceDate, options).aggregate;
}

/**
 * Map the canonical phases off a `ProjectIntent` to the lightweight
 * `ProjectPhaseLike[]` the per-phase engine needs. Returns the multi-phase
 * list ONLY when the intent is genuinely phased (`effectivePhases().isPhased`);
 * for single-phase / legacy intents it returns `undefined` so callers fall back
 * to the single-COD path. Pure; never throws.
 *
 * Imported lazily by surfaces that already hold a `ProjectIntent`. Kept here so
 * the mapping rule lives next to the engine that consumes it.
 */
export function projectPhasesForCriticalPath(
  intent: import('../types/siteRag').ProjectIntent | null | undefined,
): ProjectPhaseLike[] | undefined {
  const phases = intent?.phases;
  if (!Array.isArray(phases) || phases.length < 2) return undefined;
  return phases.map(p => ({
    id: p.id,
    sequence: p.sequence,
    targetInServiceDate: p.targetInServiceDate ?? null,
  }));
}
